import type { LangChainMessage } from '@assistant-ui/react-langgraph';

/**
 * Tool names used by backend middlewares (after_agent hooks) for internal
 * structured-output LLM calls.  These should never be displayed in the UI.
 */
export const INTERNAL_TOOL_NAMES = new Set([
  '_ArtifactLinkingResult', // legacy (old threads)
  '_ExternalAssetLinkingResult', // new
  'ConversationMetadata',
  'get_topics_content',
  'get_skills_content',
]);

const BACKGROUND_GUIDANCE_KICKOFF_MARKER = '[background-guidance-kickoff]';
const BACKGROUND_GUIDANCE_APPROVAL_MARKER = '[background-guidance-approval]';
const BACKGROUND_GUIDANCE_PROPOSAL_ENFORCEMENT_MARKER =
  '[background-guidance-proposal-enforcement]';
const BACKGROUND_GUIDANCE_TRIGGER_PREFIX = 'Run an asynchronous background guidance review for ';
const BACKGROUND_GUIDANCE_TRIGGER_PHRASES = [
  'This is not a support conversation and not an autonomous publishing workflow.',
  'surface them as numbered proposals for human review in the inbox.',
  'Pause with `propose_changes` before any mutation happens',
] as const;
const BACKGROUND_GUIDANCE_PROPOSAL_TOOL = 'propose_changes';

function extractMessageText(message: LangChainMessage): string {
  const content = (message as LangChainMessage & { content?: unknown }).content;
  if (typeof content === 'string') return content;
  if (!Array.isArray(content)) return '';

  return content
    .flatMap((part) => {
      if (typeof part === 'string') return [part];
      if (
        part &&
        typeof part === 'object' &&
        'type' in part &&
        (part as { type?: unknown }).type === 'text' &&
        'text' in part &&
        typeof (part as { text?: unknown }).text === 'string'
      ) {
        return [(part as { text: string }).text];
      }
      return [];
    })
    .join('\n');
}

export function isHiddenBackgroundBootstrapMessage(message: LangChainMessage): boolean {
  if (message.type !== 'human') return false;

  const normalized = extractMessageText(message).replace(/\s+/g, ' ').trim();
  if (!normalized) return false;
  if (normalized.includes(BACKGROUND_GUIDANCE_KICKOFF_MARKER)) return true;
  if (normalized.includes(BACKGROUND_GUIDANCE_APPROVAL_MARKER)) return true;
  if (normalized.includes(BACKGROUND_GUIDANCE_PROPOSAL_ENFORCEMENT_MARKER)) return true;

  return (
    normalized.startsWith(BACKGROUND_GUIDANCE_TRIGGER_PREFIX) &&
    BACKGROUND_GUIDANCE_TRIGGER_PHRASES.every((phrase) => normalized.includes(phrase))
  );
}

export function filterHiddenBackgroundBootstrapMessages(
  messages: LangChainMessage[],
  opts?: { assumeBackgroundThread?: boolean },
): LangChainMessage[] {
  const visibleMessages: LangChainMessage[] = [];
  let inBootstrapPrefix = true;

  for (const message of messages) {
    if (isHiddenBackgroundBootstrapMessage(message)) {
      continue;
    }

    if (inBootstrapPrefix && opts?.assumeBackgroundThread && message.type === 'human') {
      continue;
    }

    inBootstrapPrefix = false;
    visibleMessages.push(message);
  }

  return visibleMessages;
}

export function hasBackgroundBootstrapThreadMarker(messages: LangChainMessage[]): boolean {
  return messages.some((message) => isHiddenBackgroundBootstrapMessage(message));
}

function getToolCallName(toolCall: unknown): string | undefined {
  if (!toolCall || typeof toolCall !== 'object') return undefined;

  if ('name' in toolCall && typeof (toolCall as { name?: unknown }).name === 'string') {
    return (toolCall as { name: string }).name;
  }

  if (
    'function' in toolCall &&
    toolCall.function &&
    typeof toolCall.function === 'object' &&
    'name' in toolCall.function &&
    typeof (toolCall.function as { name?: unknown }).name === 'string'
  ) {
    return (toolCall.function as { name: string }).name;
  }

  return undefined;
}

function isBackgroundProposalBoundaryMessage(message: LangChainMessage): boolean {
  if (
    message.type === 'tool' &&
    'name' in message &&
    message.name === BACKGROUND_GUIDANCE_PROPOSAL_TOOL
  ) {
    return true;
  }

  return (
    message.type === 'ai' &&
    'tool_calls' in message &&
    Array.isArray(message.tool_calls) &&
    message.tool_calls.some(
      (toolCall) => getToolCallName(toolCall) === BACKGROUND_GUIDANCE_PROPOSAL_TOOL,
    )
  );
}

export function filterBackgroundPreProposalMessages(
  messages: LangChainMessage[],
  opts?: { assumeBackgroundThread?: boolean },
): LangChainMessage[] {
  if (!opts?.assumeBackgroundThread && !hasBackgroundBootstrapThreadMarker(messages)) {
    return messages;
  }

  const proposalStartIndex = messages.findIndex(isBackgroundProposalBoundaryMessage);
  if (proposalStartIndex === -1) return messages;

  return messages.slice(proposalStartIndex);
}

export function stripBackgroundProposalNarrative(
  messages: LangChainMessage[],
  opts?: { assumeBackgroundThread?: boolean },
): LangChainMessage[] {
  if (!opts?.assumeBackgroundThread && !hasBackgroundBootstrapThreadMarker(messages)) {
    return messages;
  }

  let strippedFirstProposalBoundary = false;
  return messages.map((message) => {
    if (!isBackgroundProposalBoundaryMessage(message) || message.type !== 'ai') return message;
    if (strippedFirstProposalBoundary) return message;

    strippedFirstProposalBoundary = true;

    const content = (message as LangChainMessage & { content?: unknown }).content;
    let nextContent: (LangChainMessage & { content?: unknown })['content'] = '';

    if (Array.isArray(content)) {
      nextContent = content.filter((part) => {
        if (typeof part === 'string') return false;
        if (!part || typeof part !== 'object') return true;
        return !('type' in part) || (part as { type?: unknown }).type !== 'text';
      });
    }

    return {
      ...message,
      content: nextContent,
    } as LangChainMessage;
  });
}

/**
 * Strip internal middleware tool calls/results so assistant-ui never sees them.
 * - Removes tool result messages whose name matches an internal tool.
 * - Removes the matching tool_call entries from AI messages (keeps the AI
 *   message itself if it still has content or other tool calls).
 */
export function filterInternalToolMessages(messages: LangChainMessage[]): LangChainMessage[] {
  // Collect tool_call IDs that belong to internal tools
  const internalCallIds = new Set<string>();
  for (const msg of messages) {
    if (msg.type === 'ai' && 'tool_calls' in msg && Array.isArray(msg.tool_calls)) {
      for (const tc of msg.tool_calls) {
        if (tc.name && INTERNAL_TOOL_NAMES.has(tc.name)) {
          internalCallIds.add(tc.id);
        }
      }
    }
  }

  if (internalCallIds.size === 0) return messages;

  return messages
    .filter((msg) => {
      // Drop tool result messages for internal calls
      if (msg.type === 'tool' && 'tool_call_id' in msg) {
        if (msg.tool_call_id && internalCallIds.has(msg.tool_call_id)) return false;
      }
      return true;
    })
    .map((msg) => {
      // Strip internal tool_calls from AI messages
      if (msg.type === 'ai' && 'tool_calls' in msg && Array.isArray(msg.tool_calls)) {
        if (msg.tool_calls.some((tc: { id: string }) => internalCallIds.has(tc.id))) {
          return {
            ...msg,
            tool_calls: msg.tool_calls.filter((tc: { id: string }) => !internalCallIds.has(tc.id)),
          } as LangChainMessage;
        }
      }
      return msg;
    });
}

/**
 * Remove orphaned tool result messages that can occur after a run interruption
 * (double-texting). Only removes tool result messages whose tool_call_id
 * doesn't match any tool call in an AI message.
 */
export function removeOrphanedToolMessages(messages: LangChainMessage[]): LangChainMessage[] {
  const allToolCallIds = new Set<string>();
  for (const msg of messages) {
    if (msg.type === 'ai' && Array.isArray((msg as { tool_calls?: unknown[] }).tool_calls)) {
      for (const tc of (msg as { tool_calls: { id: string }[] }).tool_calls) {
        if (tc.id) allToolCallIds.add(tc.id);
      }
    }
  }

  return messages.filter((msg) => {
    if (msg.type === 'tool' && 'tool_call_id' in msg) {
      if (msg.tool_call_id && !allToolCallIds.has(msg.tool_call_id as string)) return false;
    }
    return true;
  });
}

/**
 * LangGraph can stream assistant tool calls first as Anthropic-style content
 * parts (`tool_use`) before the mirrored top-level `tool_calls` array is
 * populated. assistant-ui only reads the top-level tool_calls field, so
 * synthesize it from content when needed.
 */
export function hydrateAiToolCallsFromContent(messages: LangChainMessage[]): LangChainMessage[] {
  return messages.map((msg) => {
    if (msg.type !== 'ai' || !Array.isArray((msg as { content?: unknown }).content)) return msg;

    const content = (msg as { content: unknown[] }).content;
    const extracted = content.flatMap((part) => {
      if (!part || typeof part !== 'object' || Array.isArray(part)) return [];
      const toolUse = part as Record<string, unknown>;
      if (toolUse.type !== 'tool_use') return [];

      const id = typeof toolUse.id === 'string' ? toolUse.id : undefined;
      const name = typeof toolUse.name === 'string' ? toolUse.name : undefined;
      if (!id || !name) return [];

      const input =
        toolUse.input && typeof toolUse.input === 'object' && !Array.isArray(toolUse.input)
          ? (toolUse.input as Record<string, unknown>)
          : {};
      const partialJson =
        typeof toolUse.partial_json === 'string'
          ? toolUse.partial_json
          : JSON.stringify(input);

      return [
        {
          id,
          name,
          args: input,
          type: 'tool_call' as const,
          partial_json: partialJson,
        },
      ];
    });

    if (extracted.length === 0) return msg;

    const existing = Array.isArray((msg as { tool_calls?: unknown[] }).tool_calls)
      ? ((msg as { tool_calls: Record<string, unknown>[] }).tool_calls)
      : [];
    const existingIds = new Set(
      existing.flatMap((tc) => (typeof tc?.id === 'string' ? [tc.id] : [])),
    );
    const merged = [...existing, ...extracted.filter((tc) => !existingIds.has(tc.id))];

    return {
      ...msg,
      tool_calls: merged,
    } as LangChainMessage;
  });
}

/**
 * Some streaming payloads can surface a tool result before the matching AI
 * tool-call shell in the same batch. assistant-ui expects the assistant
 * message first, so normalize that local ordering before conversion.
 */
export function reorderOutOfOrderToolMessages(messages: LangChainMessage[]): LangChainMessage[] {
  const assistantIndexByToolCallId = new Map<string, number>();

  messages.forEach((msg, index) => {
    if (msg.type !== 'ai' || !Array.isArray((msg as { tool_calls?: unknown[] }).tool_calls)) return;
    for (const tc of (msg as { tool_calls: { id?: string }[] }).tool_calls) {
      if (tc.id) assistantIndexByToolCallId.set(tc.id, index);
    }
  });

  const deferredTools = new Map<number, LangChainMessage[]>();
  const reordered: LangChainMessage[] = [];

  messages.forEach((msg, index) => {
    if (msg.type === 'tool' && 'tool_call_id' in msg && msg.tool_call_id) {
      const assistantIndex = assistantIndexByToolCallId.get(msg.tool_call_id as string);
      if (assistantIndex != null && assistantIndex > index) {
        const bucket = deferredTools.get(assistantIndex) ?? [];
        bucket.push(msg);
        deferredTools.set(assistantIndex, bucket);
        return;
      }
    }

    reordered.push(msg);
    const waitingTools = deferredTools.get(index);
    if (waitingTools?.length) {
      reordered.push(...waitingTools);
      deferredTools.delete(index);
    }
  });

  return reordered;
}

/**
 * Fix tool result messages that have a missing/null name by matching them
 * to the corresponding tool_call in the preceding AI message.
 */
export function sanitizeToolMessages(messages: LangChainMessage[]): LangChainMessage[] {
  const toolCallNames = new Map<string, string>();

  for (const msg of messages) {
    if (msg.type === 'ai' && Array.isArray((msg as { tool_calls?: unknown[] }).tool_calls)) {
      for (const tc of (msg as { tool_calls: { id: string; name: string }[] }).tool_calls) {
        if (tc.id && tc.name) toolCallNames.set(tc.id, tc.name);
      }
    }
  }

  return messages.map((msg) => {
    if (msg.type === 'tool' && !msg.name) {
      const toolMsg = msg as { tool_call_id?: string };
      const name = toolMsg.tool_call_id ? toolCallNames.get(toolMsg.tool_call_id) : undefined;
      if (name) return { ...msg, name };
    }
    return msg;
  });
}

/**
 * Canonical frontend sanitization pipeline before handing messages to assistant-ui.
 * Order matters:
 * 1) Fill missing tool names from corresponding AI tool_calls.
 * 2) Drop orphaned tool results that violate assistant-ui invariants.
 * 3) Strip internal middleware tool calls/results from user-visible history.
 */
export function sanitizeMessagesForAssistantUi(
  messages: LangChainMessage[],
  opts?: { assumeBackgroundThread?: boolean },
): LangChainMessage[] {
  return filterHiddenBackgroundBootstrapMessages(
    filterBackgroundPreProposalMessages(
      stripBackgroundProposalNarrative(
        filterInternalToolMessages(
          removeOrphanedToolMessages(
            reorderOutOfOrderToolMessages(
              sanitizeToolMessages(hydrateAiToolCallsFromContent(messages)),
            ),
          ),
        ),
        {
          assumeBackgroundThread: opts?.assumeBackgroundThread,
        },
      ),
      {
        assumeBackgroundThread: opts?.assumeBackgroundThread,
      },
    ),
    {
      assumeBackgroundThread: opts?.assumeBackgroundThread,
    },
  );
}
